import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, TrendingUp, TrendingDown, Minus, RefreshCw,
  BarChart3, Activity, Gauge, ChevronRight, DollarSign,
  Bitcoin, LineChart, Gem, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Gauge Meter Component
const GaugeMeter = ({ score, signal }) => {
  // score is -100 to +100, normalize to 0-180 degrees
  const rotation = ((score + 100) / 200) * 180 - 90;
  
  const getSignalColor = () => {
    switch (signal) {
      case 'strong_buy': return '#22c55e';
      case 'buy': return '#4ade80';
      case 'neutral': return '#94a3b8';
      case 'sell': return '#f87171';
      case 'strong_sell': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getSignalLabel = () => {
    switch (signal) {
      case 'strong_buy': return 'STRONG BUY';
      case 'buy': return 'BUY';
      case 'neutral': return 'NEUTRAL';
      case 'sell': return 'SELL';
      case 'strong_sell': return 'STRONG SELL';
      default: return 'NEUTRAL';
    }
  };

  return (
    <div className="relative flex flex-col items-center" data-testid="gauge-meter">
      {/* Gauge Background */}
      <div className="relative w-48 h-24 overflow-hidden">
        {/* Gauge Arc */}
        <div 
          className="absolute inset-0 rounded-t-full border-8 border-b-0"
          style={{
            background: `conic-gradient(from -90deg at 50% 100%, 
              #ef4444 0deg, 
              #f87171 36deg, 
              #fbbf24 72deg, 
              #94a3b8 108deg, 
              #4ade80 144deg, 
              #22c55e 180deg, 
              transparent 180deg)`
          }}
        />
        
        {/* Inner circle to create donut effect */}
        <div className="absolute inset-3 bg-card rounded-t-full" />
        
        {/* Needle */}
        <div 
          className="absolute bottom-0 left-1/2 w-1 h-20 origin-bottom transition-transform duration-700"
          style={{ 
            transform: `translateX(-50%) rotate(${rotation}deg)`,
            background: 'linear-gradient(to top, transparent 0%, #fff 30%)'
          }}
        >
          <div 
            className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full"
            style={{ backgroundColor: getSignalColor() }}
          />
        </div>
        
        {/* Center dot */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-4 h-4 rounded-full bg-card border-2 border-border" />
      </div>
      
      {/* Labels */}
      <div className="flex justify-between w-48 text-xs text-muted-foreground mt-1 px-2">
        <span>Strong Sell</span>
        <span>Strong Buy</span>
      </div>
      
      {/* Signal Value */}
      <div className="mt-4 text-center">
        <p 
          className="font-heading text-2xl font-bold uppercase tracking-wider"
          style={{ color: getSignalColor() }}
          data-testid="signal-label"
        >
          {getSignalLabel()}
        </p>
        <p className="text-sm text-muted-foreground font-mono">Score: {score.toFixed(1)}</p>
      </div>
    </div>
  );
};

// Signal Counter Component
const SignalCounter = ({ buy, sell, neutral, title }) => {
  const total = buy + sell + neutral;
  const buyWidth = (buy / total) * 100;
  const sellWidth = (sell / total) * 100;
  
  return (
    <div className="space-y-2" data-testid={`signal-counter-${title.toLowerCase().replace(' ', '-')}`}>
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{title}</span>
        <div className="flex gap-4 font-mono text-xs">
          <span className="text-primary">{buy} Buy</span>
          <span className="text-muted-foreground">{neutral} Neutral</span>
          <span className="text-destructive">{sell} Sell</span>
        </div>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden flex">
        <div className="h-full bg-primary" style={{ width: `${buyWidth}%` }} />
        <div className="h-full bg-muted-foreground/50" style={{ width: `${100 - buyWidth - sellWidth}%` }} />
        <div className="h-full bg-destructive" style={{ width: `${sellWidth}%` }} />
      </div>
    </div>
  );
};

// Indicator Row Component
const IndicatorRow = ({ name, value, signal }) => {
  const getSignalBadge = () => {
    switch (signal) {
      case 'buy':
        return <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">BUY</Badge>;
      case 'sell':
        return <Badge className="bg-destructive/20 text-destructive border-destructive/30 text-xs">SELL</Badge>;
      default:
        return <Badge className="bg-muted text-muted-foreground text-xs">NEUTRAL</Badge>;
    }
  };

  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground">{name}</span>
      <div className="flex items-center gap-3">
        <span className="font-mono text-sm">{typeof value === 'number' ? value.toFixed(5) : value}</span>
        {getSignalBadge()}
      </div>
    </div>
  );
};

// Instrument Card for Summary View
const InstrumentCard = ({ instrument, onClick }) => {
  const getSignalColor = (signal) => {
    switch (signal) {
      case 'strong_buy': return 'text-primary border-primary/50';
      case 'buy': return 'text-primary/80 border-primary/30';
      case 'neutral': return 'text-muted-foreground border-border';
      case 'sell': return 'text-destructive/80 border-destructive/30';
      case 'strong_sell': return 'text-destructive border-destructive/50';
      default: return 'text-muted-foreground border-border';
    }
  };

  const getSignalText = (signal) => {
    return signal?.replace('_', ' ').toUpperCase() || 'NEUTRAL';
  };

  return (
    <Card 
      className={`bg-card hover:bg-secondary/20 cursor-pointer transition-all ${getSignalColor(instrument.overall_signal)}`}
      onClick={onClick}
      data-testid={`instrument-card-${instrument.symbol}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-heading font-bold">{instrument.symbol}</p>
              {instrument.is_live && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded animate-pulse">
                  LIVE
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{instrument.name}</p>
          </div>
          <div className="text-right">
            <p className={`font-heading font-bold text-sm ${getSignalColor(instrument.overall_signal)}`}>
              {getSignalText(instrument.overall_signal)}
            </p>
            <p className="text-xs text-muted-foreground font-mono">
              {instrument.buy_count}B / {instrument.sell_count}S
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TechnicalAnalysis = () => {
  const navigate = useNavigate();
  const [instruments, setInstruments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    fetchInstruments();
    fetchAllSummary();
  }, []);

  const fetchInstruments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/technical-analysis/instruments`);
      const data = await response.json();
      setCategories(data.categories);
    } catch (error) {
      console.error('Failed to fetch instruments');
    }
  };

  const fetchAllSummary = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/technical-analysis/summary/all`);
      const data = await response.json();
      setInstruments(data.instruments);
    } catch (error) {
      toast.error('Failed to fetch technical analysis data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysis = async (symbol) => {
    setAnalysisLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/technical-analysis/${symbol}`);
      const data = await response.json();
      setAnalysis(data);
      setSelectedSymbol(symbol);
    } catch (error) {
      toast.error('Failed to fetch analysis');
    } finally {
      setAnalysisLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'forex': return <DollarSign className="w-4 h-4" />;
      case 'crypto': return <Bitcoin className="w-4 h-4" />;
      case 'indices': return <LineChart className="w-4 h-4" />;
      case 'commodities': return <Gem className="w-4 h-4" />;
      default: return <BarChart3 className="w-4 h-4" />;
    }
  };

  const filteredInstruments = categoryFilter === 'all' 
    ? instruments 
    : instruments.filter(i => i.category === categoryFilter);

  // Summary stats
  const buySignals = instruments.filter(i => i.overall_signal === 'buy' || i.overall_signal === 'strong_buy').length;
  const sellSignals = instruments.filter(i => i.overall_signal === 'sell' || i.overall_signal === 'strong_sell').length;
  const neutralSignals = instruments.filter(i => i.overall_signal === 'neutral').length;

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="technical-analysis-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Back to Dashboard Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="mb-6 border-white/10 text-white hover:bg-white/5"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Activity className="w-8 h-8 text-cyan-400" />
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-white" data-testid="ta-title">
              Market <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Signals</span>
            </h1>
          </div>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
            Real-time buy/sell signals based on moving averages and oscillators. Make informed trading decisions.
          </p>
        </motion.div>

        {/* Market Overview Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-3 md:gap-4 mb-6"
        >
          <Card className="bg-card border-primary/30">
            <CardContent className="p-3 md:p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-xs md:text-sm text-muted-foreground">Bullish</span>
              </div>
              <p className="font-mono text-2xl md:text-3xl font-bold text-primary mt-1">{buySignals}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-muted">
            <CardContent className="p-3 md:p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <Minus className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs md:text-sm text-muted-foreground">Neutral</span>
              </div>
              <p className="font-mono text-2xl md:text-3xl font-bold text-muted-foreground mt-1">{neutralSignals}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-destructive/30">
            <CardContent className="p-3 md:p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <TrendingDown className="w-4 h-4 text-destructive" />
                <span className="text-xs md:text-sm text-muted-foreground">Bearish</span>
              </div>
              <p className="font-mono text-2xl md:text-3xl font-bold text-destructive mt-1">{sellSignals}</p>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Instruments List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="lg:col-span-1"
          >
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg uppercase">Instruments</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={fetchAllSummary}
                    disabled={loading}
                    data-testid="refresh-instruments"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                
                {/* Category Filter */}
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full bg-input/50 mt-2" data-testid="category-filter">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="forex">Forex</SelectItem>
                    <SelectItem value="crypto">Crypto</SelectItem>
                    <SelectItem value="indices">Indices</SelectItem>
                    <SelectItem value="commodities">Commodities</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent className="max-h-[600px] overflow-y-auto space-y-2">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : (
                  filteredInstruments.map((instrument) => (
                    <InstrumentCard 
                      key={instrument.symbol} 
                      instrument={instrument}
                      onClick={() => fetchAnalysis(instrument.symbol)}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Analysis Detail */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            {analysisLoading ? (
              <Card className="bg-card border-border">
                <CardContent className="flex items-center justify-center py-24">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </CardContent>
              </Card>
            ) : analysis ? (
              <div className="space-y-4">
                {/* Gauge and Summary */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                          {getCategoryIcon(analysis.instrument.category)}
                          {analysis.instrument.name}
                          {analysis.is_live && (
                            <span className="px-2 py-1 text-xs font-bold bg-red-500 text-white rounded animate-pulse">
                              LIVE
                            </span>
                          )}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {analysis.symbol} • Current Price: <span className="font-mono">{analysis.current_price.toFixed(5)}</span>
                          {analysis.data_source && (
                            <span className="ml-2 text-xs">({analysis.data_source})</span>
                          )}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => fetchAnalysis(analysis.symbol)}
                        data-testid="refresh-analysis"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-8 items-center">
                      {/* Gauge */}
                      <div className="flex justify-center">
                        <GaugeMeter 
                          score={analysis.summary.signal_score} 
                          signal={analysis.summary.overall_signal}
                        />
                      </div>
                      
                      {/* Signal Summary */}
                      <div className="space-y-4">
                        <SignalCounter 
                          title="Moving Averages"
                          buy={analysis.moving_averages.summary.buy}
                          sell={analysis.moving_averages.summary.sell}
                          neutral={analysis.moving_averages.summary.neutral}
                        />
                        <SignalCounter 
                          title="Oscillators"
                          buy={analysis.oscillators.summary.buy}
                          sell={analysis.oscillators.summary.sell}
                          neutral={analysis.oscillators.summary.neutral}
                        />
                        <div className="pt-2 border-t border-border">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Total Signals</span>
                            <div className="flex gap-4 font-mono">
                              <span className="text-primary font-bold">{analysis.summary.total_buy} Buy</span>
                              <span className="text-muted-foreground">{analysis.summary.total_neutral} Neutral</span>
                              <span className="text-destructive font-bold">{analysis.summary.total_sell} Sell</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Detailed Indicators */}
                <Tabs defaultValue="moving-averages" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 bg-secondary/30">
                    <TabsTrigger value="moving-averages" data-testid="tab-ma">Moving Averages</TabsTrigger>
                    <TabsTrigger value="oscillators" data-testid="tab-osc">Oscillators</TabsTrigger>
                    <TabsTrigger value="pivots" data-testid="tab-pivots">Pivot Points</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="moving-averages">
                    <Card className="bg-card border-border">
                      <CardContent className="pt-6">
                        {analysis.moving_averages.signals.map((indicator, i) => (
                          <IndicatorRow 
                            key={i}
                            name={indicator.name}
                            value={indicator.value}
                            signal={indicator.signal}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  <TabsContent value="oscillators">
                    <Card className="bg-card border-border">
                      <CardContent className="pt-6">
                        {analysis.oscillators.signals.map((indicator, i) => (
                          <IndicatorRow 
                            key={i}
                            name={indicator.name}
                            value={indicator.value}
                            signal={indicator.signal}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  <TabsContent value="pivots">
                    <Card className="bg-card border-border">
                      <CardContent className="pt-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-heading uppercase mb-3 text-primary">Resistance</h4>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">R3</span>
                                <span className="font-mono">{analysis.pivot_points.classic.r3}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">R2</span>
                                <span className="font-mono">{analysis.pivot_points.classic.r2}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">R1</span>
                                <span className="font-mono">{analysis.pivot_points.classic.r1}</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-heading uppercase mb-3 text-destructive">Support</h4>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">S1</span>
                                <span className="font-mono">{analysis.pivot_points.classic.s1}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">S2</span>
                                <span className="font-mono">{analysis.pivot_points.classic.s2}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">S3</span>
                                <span className="font-mono">{analysis.pivot_points.classic.s3}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="flex justify-between text-sm">
                            <span className="font-heading uppercase">Pivot Point</span>
                            <span className="font-mono font-bold">{analysis.pivot_points.classic.pivot}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="flex flex-col items-center justify-center py-24 text-center">
                  <Gauge className="w-16 h-16 text-muted-foreground mb-4" />
                  <h3 className="font-heading text-xl font-bold mb-2">Select an Instrument</h3>
                  <p className="text-muted-foreground text-sm max-w-sm">
                    Click on any instrument from the list to view detailed technical analysis with buy/sell signals.
                  </p>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground text-center">
                <strong>Disclaimer:</strong> Technical analysis signals are for informational purposes only and should not be considered as financial advice. 
                Past performance does not guarantee future results. Always conduct your own research and consider your risk tolerance before trading.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default TechnicalAnalysis;
